import type { TilePosition } from '../../../model/gamestate/GameState';
import type { MapLayerRenderer } from '../MapLayerRenderer';
import type { Viewport } from '../viewport';
import { COLORS } from '../constants';

export class ActiveRangeOverlayRenderer {
    private activeRangeCenter: TilePosition | null = null;

    constructor(
        private readonly tileSize: number,
        private readonly getActiveRadius: () => number,
    ) {}

    public setActiveRangeCenter(position: TilePosition | null): void {
        this.activeRangeCenter = position ? { ...position } : null;
    }

    public getActiveRangeCenter(): TilePosition | null {
        return this.activeRangeCenter;
    }

    public drawActiveTiles(ctx: CanvasRenderingContext2D, mapLayer: MapLayerRenderer, viewport: Viewport): void {
        if (!this.activeRangeCenter) {
            return;
        }

        ctx.save();
        this.drawActiveTilesForCenter(ctx, this.activeRangeCenter, 0.06, mapLayer, viewport);
        ctx.restore();
    }

    public drawHoverHighlight(
        ctx: CanvasRenderingContext2D,
        interactionInfo: { tile: TilePosition | null; inFog: boolean },
        mapLayer: MapLayerRenderer,
        viewport: Viewport
    ): void {
        if (!interactionInfo.tile) {
            return;
        }
        const { x, y } = interactionInfo.tile;
        const grid = mapLayer.getGrid();
        const screen = viewport.worldToScreen(x, y, grid);
        const cx = screen.x + this.tileSize / 2;
        const cy = screen.y + this.tileSize / 2;
        const halfSize = this.tileSize * 0.5;

        ctx.save();
        ctx.fillStyle = interactionInfo.inFog ? 'rgba(255, 255, 255, 0.04)' : COLORS.hoverFill;
        grid.fillTile(ctx, cx, cy, halfSize * 0.95, x, y);

        ctx.strokeStyle = interactionInfo.inFog ? 'rgba(255, 255, 255, 0.25)' : COLORS.hoverStroke;
        ctx.lineWidth = 2;
        grid.drawTilePath(ctx, cx, cy, halfSize * 0.95, x, y);
        ctx.stroke();
        ctx.restore();
    }

    private drawActiveTilesForCenter(
        ctx: CanvasRenderingContext2D,
        center: TilePosition,
        alpha: number,
        mapLayer: MapLayerRenderer,
        viewport: Viewport
    ): void {
        const grid = mapLayer.getGrid();
        const map = mapLayer.getMap();
        const radius = this.getActiveRadius();
        const cx = Math.floor(center.x);
        const cy = Math.floor(center.y);
        const minX = Math.max(0, cx - radius);
        const maxX = Math.min(map.width - 1, cx + radius);
        const minY = Math.max(0, cy - radius);
        const maxY = Math.min(map.height - 1, cy + radius);

        ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
        for (let y = minY; y <= maxY; y += 1) {
            for (let x = minX; x <= maxX; x += 1) {
                if (grid.distance(cx, cy, x, y) > radius) {
                    continue;
                }
                const screen = viewport.worldToScreen(x, y, grid);
                const tileCx = screen.x + this.tileSize / 2;
                const tileCy = screen.y + this.tileSize / 2;
                grid.fillTile(ctx, tileCx, tileCy, this.tileSize * 0.5, x, y);
            }
        }
    }
}
